import { useCallback, useSyncExternalStore } from 'react';
import { ancestorsOf, isEffectivelyLocked, type ObjectData, type SceneStore } from '@os/doc';
import type { EditorController } from './controller';
import { graphOf } from './graph-of';

type Of<T extends ObjectData['type']> = Extract<ObjectData, { type: T }>;

/** What the properties sheet and the context bar know about the selection (P5f: shared by both). */
export type SelectionProps = {
  objects: ObjectData[];
  ids: string[];
  ofType: (type: ObjectData['type']) => string[];
  texts: ObjectData[];
  shapes: ObjectData[];
  /** Type-specific lists hold every selected object, or none (a mixed selection shares no such control). */
  sections: Of<'section'>[];
  connectors: Of<'connector'>[];
  images: Of<'image'>[];
  lockedOf: (o: ObjectData) => boolean;
  allLocked: boolean;
  /** Locked by a section around it: the section's padlock unlocks it, not the lock toggle (P5d). */
  inherited: boolean;
  /** The section the whole selection sits in, if they share it. */
  home: ObjectData | undefined;
  current: unknown;
  setColor: (hex: string) => void;
};

/** Where each type keeps its colour: stickies and sections have a field, the others a style key (task lists: `accent`). */
export function colorOf(o: Readonly<ObjectData>): unknown {
  if (o.type === 'sticky') return o.color;
  if (o.type === 'section') return o.fill;
  if (o.type === 'tasklist') return o.style.accent;
  return o.style[o.type === 'shape' ? 'fill' : o.type === 'text' ? 'color' : 'stroke'];
}

/** The live selection and what can be done to it; null without a selection. Edits go through store commands only. */
export function useSelectionProps(controller: EditorController, store: SceneStore): SelectionProps | null {
  const ui = useSyncExternalStore(controller.subscribeUi, controller.ui);
  const subscribeList = useCallback((cb: () => void) => store.subscribeList(cb), [store]);
  const all = useSyncExternalStore(subscribeList, () => store.list());
  const selected = new Set(ui.selection);
  const objects = all.filter((o) => selected.has(o.id));
  if (!objects.length) return null;
  const ids = objects.map((o) => o.id);
  const ofType = (type: ObjectData['type']) => objects.filter((o) => o.type === type).map((o) => o.id);
  const every = <T extends ObjectData['type']>(type: T): Of<T>[] => (objects.every((o) => o.type === type) ? (objects as Of<T>[]) : []);
  const g = graphOf(store, all);
  const lockedOf = (o: ObjectData) => isEffectivelyLocked(g, (id) => store.getObject(id), o.id);
  const homes = new Set(ids.map((id) => ancestorsOf(g, id).find((a) => store.getObject(a)?.type === 'section') ?? null));
  const homeId = homes.size === 1 ? [...homes][0] : undefined;
  const setColor = (hex: string) => {
    const c = store.commands;
    if (ofType('sticky').length) c.setProps(ofType('sticky'), { color: hex });
    if (ofType('shape').length) c.setStyle(ofType('shape'), { fill: hex });
    if (ofType('text').length) c.setStyle(ofType('text'), { color: hex });
    if (ofType('arrow').length) c.setStyle(ofType('arrow'), { stroke: hex });
    if (ofType('connector').length) c.setStyle(ofType('connector'), { stroke: hex });
    if (ofType('section').length) c.setProps(ofType('section'), { fill: hex });
    if (ofType('tasklist').length) c.setStyle(ofType('tasklist'), { accent: hex });
  };
  return {
    objects,
    ids,
    ofType,
    texts: objects.filter((o) => o.type === 'text'),
    shapes: objects.filter((o) => o.type === 'shape'),
    sections: every('section'),
    connectors: every('connector'),
    images: every('image'),
    lockedOf,
    allLocked: objects.every(lockedOf),
    inherited: objects.some((o) => lockedOf(o) && !o.locked),
    home: homeId ? all.find((x) => x.id === homeId) : undefined, // from the snapshot: a renamed section shows its new name
    current: colorOf(objects[0]!),
    setColor,
  };
}
